import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type { Project, ProjectInput, ProjectStatus } from '@stromo/shared';

export interface ProjectListItem extends Project {
  client_name: string | null;
  architect_name: string | null;
  project_manager_name: string | null;
  project_manager_abbr: string | null;
  site_manager_name: string | null;
  site_manager_abbr: string | null;
}

export interface ProjectFilters {
  q?: string;
  status?: ProjectStatus;
  project_manager_id?: number;
  site_manager_id?: number;
  is_archived?: boolean;
  sort?: string;
  direction?: 'asc' | 'desc';
}

export function useProjects(filters: ProjectFilters = {}) {
  return useQuery({
    queryKey: ['projects', filters],
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (filters.q) params.q = filters.q;
      if (filters.status) params.status = filters.status;
      if (filters.project_manager_id) params.project_manager_id = filters.project_manager_id;
      if (filters.site_manager_id) params.site_manager_id = filters.site_manager_id;
      if (filters.is_archived !== undefined) params.is_archived = String(filters.is_archived);
      if (filters.sort) params.sort = filters.sort;
      if (filters.direction) params.direction = filters.direction;
      const { data } = await api.get<{ items: ProjectListItem[] }>('/projects', { params });
      return data.items;
    },
  });
}

export function useProject(id: number | undefined) {
  return useQuery({
    queryKey: ['project', id],
    queryFn: async () => {
      const { data } = await api.get<ProjectListItem>(`/projects/${id}`);
      return data;
    },
    enabled: id !== undefined,
  });
}

/** Verändert sich ein Projekt oder Task, müssen Projektliste, Detailansicht
 *  und Gantt frische Daten ziehen. Zentrale Helfer-Funktion, damit nicht
 *  mehr jede Mutation einzeln Keys vergessen kann. */
function invalidateProjectViews(qc: ReturnType<typeof useQueryClient>, projectId?: number) {
  qc.invalidateQueries({ queryKey: ['projects'] });
  qc.invalidateQueries({ queryKey: ['gantt'] });
  if (projectId !== undefined) {
    qc.invalidateQueries({ queryKey: ['project', projectId] });
  }
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ProjectInput) => {
      const { data } = await api.post<Project>('/projects', input);
      return data;
    },
    onSuccess: (created) => invalidateProjectViews(queryClient, created.id),
  });
}

export function useUpdateProject(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<ProjectInput>) => {
      const { data } = await api.put<Project>(`/projects/${id}`, input);
      return data;
    },
    onSuccess: () => invalidateProjectViews(queryClient, id),
  });
}

export function usePatchProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: number; patch: Partial<ProjectInput> }) => {
      const { data } = await api.put<Project>(`/projects/${id}`, patch);
      return data;
    },
    onSuccess: (_data, { id }) => invalidateProjectViews(queryClient, id),
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/projects/${id}`);
      return id;
    },
    onSuccess: () => invalidateProjectViews(queryClient),
  });
}
